import { AnimatePresence, motion } from "framer-motion";
import synphaetImg from "../../assets/projects/SynphaetAcademy.png";
import newProjectImg from "../../assets/projects/new-project.png";
import project3Img from "../../assets/projects/project-3.png";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useMemo, useRef, type KeyboardEvent } from "react";
import { fetchProjects, type Project as APIProject } from "../../lib/api";
import {
  DetailTerminal,
  createMacDetail,
  type DetailPayload,
} from "../ui/DetailTerminal";
import { isVideoUrl } from "../../lib/media";
import { useTheme } from "../../contexts/ThemeContext";
import { monoInk } from "../../lib/mono";

interface Project {
  title: string;
  description: string;
  tags: string[];
  githubUrl?: string;
  liveUrl?: string;
  image?: string;
  coverUrl?: string;
  galleries?: string[];
  blur?: boolean;
  featured?: boolean;
}

function mapApiProjectToProject(apiProject: APIProject): Project {
  return {
    title: apiProject.title,
    description: apiProject.description || "",
    tags: apiProject.techStack || [],
    githubUrl: apiProject.githubUrl || undefined,
    liveUrl: apiProject.demoUrl || undefined,
    image: apiProject.imageUrl || undefined,
    coverUrl: apiProject.coverUrl || undefined,
    galleries: apiProject.galleries || [],
    blur: false,
    featured: apiProject.featured,
  };
}

const getFallbackProjects = (t: (key: string) => string): Project[] => [
  {
    title: t("proj.p6.title"),
    description: t("proj.p6.desc"),
    tags: ["Bun", "Elysia", "React", "TypeScript", "TailwindCSS", "PostgreSQL", "Docker", "Electron"],
    image: newProjectImg,
    featured: true,
  },
  {
    title: t("proj.p3.title"),
    description: t("proj.p3.desc"),
    tags: ["Java", "SQLite", "React", "TypeScript", "TailwindCSS"],
    githubUrl: "https://github.com/CS-BU167-Project/Group-Project-CS318.git",
    image: project3Img,
    featured: true,
  },
  {
    title: t("proj.p1.title"),
    description: t("proj.p1.desc"),
    tags: ["Go", "SQL", "Docker", "React", "TailwindCSS", "TypeScript"],
    image: synphaetImg,
  },
  {
    title: t("proj.p4.title"),
    description: t("proj.p4.desc"),
    tags: ["React", "TypeScript", "TailwindCSS", "SQL", "Docker"],
    image: project3Img,
  },
  {
    title: t("proj.p2.title"),
    description: t("proj.p2.desc"),
    tags: ["Go", "Docker", "SQL", "React", "TailwindCSS", "TypeScript"],
    image: newProjectImg,
  },
  {
    title: t("proj.p5.title"),
    description: t("proj.p5.desc"),
    tags: ["React", "TypeScript", "TailwindCSS", "SQL", "Docker", "Framer Motion"],
    image: synphaetImg,
  },
];

const getMainImage = (p: Project) => p.coverUrl || p.image;

/** Prefer a still image for small previews so video covers aren't decoded thrice. */
function getStillSrc(p: Project): string | undefined {
  for (const url of [p.image, ...(p.galleries || []), p.coverUrl]) {
    if (url && !isVideoUrl(url)) return url;
  }
  return undefined;
}

function getProjectImages(p: Project): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of [p.coverUrl, p.image, ...(p.galleries || [])]) {
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

function buildProjectDetail(project: Project): DetailPayload {
  const images = getProjectImages(project);
  return createMacDetail({
    kind: "project",
    title: project.title,
    description: project.description,
    tags: project.tags,
    links: {
      demo: project.liveUrl,
      github: project.githubUrl,
    },
    image: images[0],
    images,
    imageBlur: project.blur,
  });
}

function genreLine(tags: string[]) {
  return tags.slice(0, 2).join(", ") || "Project";
}

function PlayGlyph({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8.5 6.5v11l9-5.5-9-5.5z" />
    </svg>
  );
}

function PlayButton({ size = "lg" }: { size?: "lg" | "sm" }) {
  const lg = size === "lg";
  return (
    <span
      className={[
        "pointer-events-none inline-flex items-center justify-center rounded-full",
        "border border-white/80 text-white bg-black/25 backdrop-blur-[2px]",
        "shadow-[0_0_0_1px_rgba(0,0,0,0.2)]",
        lg ? "h-14 w-14 sm:h-16 sm:w-16" : "h-8 w-8 sm:h-9 sm:w-9",
      ].join(" ")}
    >
      <PlayGlyph className={lg ? "w-6 h-6 ml-0.5" : "w-3.5 h-3.5 ml-px"} />
    </span>
  );
}

function ImageFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[#111]">
      <svg
        className="w-10 h-10 text-white/25"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    </div>
  );
}

/** Cover media: featured can autoplay; thumbs use still/metadata only. */
function CoverMedia({
  src,
  blur,
  mode,
  playing = true,
  className = "",
}: {
  src: string;
  blur?: boolean;
  mode: "featured" | "thumb";
  /** When false, featured video pauses (e.g. detail modal open). */
  playing?: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const video = isVideoUrl(src);
  const mediaClass = [
    "h-full w-full object-cover",
    blur ? "blur-md scale-110" : "",
    mode === "featured"
      ? "transition duration-500 group-hover:scale-[1.02]"
      : "transition duration-500 group-hover/thumb:scale-[1.03]",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (!video || mode !== "featured") return;
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      void el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [playing, video, mode, src]);

  if (video) {
    // Thumbs: metadata only — no autoplay (avoids N concurrent decoders)
    if (mode === "thumb") {
      return (
        <video
          src={src}
          className={mediaClass}
          muted
          playsInline
          preload="metadata"
          aria-hidden
        />
      );
    }

    return (
      <video
        ref={videoRef}
        key={src}
        src={src}
        className={mediaClass}
        muted
        playsInline
        loop
        autoPlay={playing}
        preload="auto"
        aria-hidden
      />
    );
  }

  return (
    <img
      src={src}
      alt=""
      className={mediaClass}
      loading={mode === "thumb" ? "lazy" : undefined}
    />
  );
}

function StillOrPlaceholder({
  project,
  blur,
  className = "",
}: {
  project: Project;
  blur?: boolean;
  className?: string;
}) {
  const still = getStillSrc(project);
  if (still) {
    return (
      <img
        src={still}
        alt=""
        className={[
          "h-full w-full object-cover",
          blur ? "blur-sm scale-110" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      />
    );
  }
  // Video-only project: avoid a second decoder — static mark instead
  return (
    <div className="flex h-full w-full items-center justify-center bg-white/10">
      <PlayGlyph className="w-4 h-4 text-white/70" />
    </div>
  );
}

export const Projects = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { ink, muted } = monoInk(isDark);
  const [projects, setProjects] = useState<Project[]>([]);
  const [detail, setDetail] = useState<DetailPayload | null>(null);
  const [active, setActive] = useState(0);
  const listboxRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const didInitActive = useRef(false);

  const closeDetail = useCallback(() => setDetail(null), []);
  const detailOpen = detail != null;

  useEffect(() => {
    const load = async () => {
      try {
        const apiProjects = await fetchProjects();
        setProjects(apiProjects.map(mapApiProjectToProject));
      } catch {
        setProjects(getFallbackProjects(t));
      }
    };
    load();
  }, [t]);

  const list = projects.length > 0 ? projects : getFallbackProjects(t);

  // Prefer first featured on first paint; clamp if list shrinks later
  useEffect(() => {
    if (list.length === 0) {
      setActive(0);
      return;
    }
    if (!didInitActive.current) {
      const featuredIdx = list.findIndex((p) => p.featured);
      setActive(featuredIdx >= 0 ? featuredIdx : 0);
      didInitActive.current = true;
      return;
    }
    setActive((i) => Math.min(i, list.length - 1));
  }, [list]);

  const featured = list[active] ?? list[0];
  const featuredSrc = featured ? getMainImage(featured) : undefined;

  const openProject = useCallback((project: Project) => {
    setDetail(buildProjectDetail(project));
  }, []);

  const openFeatured = useCallback(() => {
    if (!featured) return;
    openProject(featured);
  }, [featured, openProject]);

  const selectActive = useCallback(
    (idx: number) => {
      if (list.length === 0) return;
      const next = ((idx % list.length) + list.length) % list.length;
      setActive(next);
      optionRefs.current[next]?.focus();
      optionRefs.current[next]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    },
    [list.length],
  );

  const onListboxKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (list.length === 0) return;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          selectActive(active + 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          selectActive(active - 1);
          break;
        case "Home":
          e.preventDefault();
          selectActive(0);
          break;
        case "End":
          e.preventDefault();
          selectActive(list.length - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (list[active]) openProject(list[active]!);
          break;
        default:
          break;
      }
    },
    [active, list, openProject, selectActive],
  );

  return (
    <section id="projects" className="apple-tile apple-tile-gallery overflow-x-clip">
      <DetailTerminal detail={detail} onClose={closeDetail} />

      <div className="mx-auto w-full max-w-[1100px] space-y-7 sm:space-y-9 md:space-y-11">
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="apple-title text-center"
          style={{ color: ink }}
        >
          {t("proj.title1")}
          {t("proj.title2")}
        </motion.h2>

        {featured && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: 0.05 }}
          >
            <button
              type="button"
              onClick={openFeatured}
              aria-label={`${t("proj.open")}: ${featured.title}`}
              className="group relative block w-full overflow-hidden rounded-2xl text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
              style={{ outlineColor: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.45)" }}
            >
              <div className="relative aspect-[16/9] w-full bg-[#0a0a0a] sm:aspect-[2/1]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${featured.title}-${active}`}
                    initial={{ opacity: 0.35 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="absolute inset-0"
                  >
                    {featuredSrc ? (
                      <CoverMedia
                        src={featuredSrc}
                        blur={featured.blur}
                        mode="featured"
                        playing={!detailOpen}
                      />
                    ) : (
                      <ImageFallback />
                    )}
                  </motion.div>
                </AnimatePresence>

                <div
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-black/10"
                  aria-hidden
                />

                {featuredSrc && isVideoUrl(featuredSrc) && (
                  <span className="absolute inset-0 flex items-center justify-center transition duration-300 group-hover:scale-105">
                    <PlayButton size="lg" />
                  </span>
                )}

                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-3.5 pb-3.5 pt-14 sm:px-5 sm:pb-4 sm:pt-16">
                  <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-white/15 bg-black/40 sm:h-12 sm:w-12">
                      <StillOrPlaceholder project={featured} blur={featured.blur} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold tracking-wide text-white uppercase sm:text-[15px]">
                        {featured.title}
                      </p>
                      <p className="truncate text-[11px] text-white/55 sm:text-[12px]">
                        {genreLine(featured.tags)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative"
        >
          <div
            ref={listboxRef}
            tabIndex={0}
            onKeyDown={onListboxKeyDown}
            className={[
              "flex gap-3 overflow-x-auto overscroll-x-contain pb-1",
              "snap-x snap-mandatory scroll-smooth",
              "-mx-1 px-1 sm:gap-3.5",
              "md:mx-0 md:grid md:grid-cols-6 md:gap-3.5 md:overflow-visible md:px-0 md:pb-0",
              "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 rounded-sm",
            ].join(" ")}
            style={{
              WebkitOverflowScrolling: "touch",
              outlineColor: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.35)",
            }}
            role="listbox"
            aria-label={t("proj.gallery")}
            aria-activedescendant={
              list[active] ? `project-option-${active}` : undefined
            }
          >
            {list.map((project, idx) => {
              const src = getMainImage(project);
              const still = getStillSrc(project);
              const selected = idx === active;
              const thumbSrc = still || src;
              return (
                <button
                  key={`${project.title}-${idx}`}
                  id={`project-option-${idx}`}
                  ref={(el) => {
                    optionRefs.current[idx] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-label={project.title}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => {
                    if (selected) {
                      openProject(project);
                    } else {
                      setActive(idx);
                    }
                  }}
                  onDoubleClick={() => openProject(project)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selected) openProject(project);
                      else setActive(idx);
                    }
                  }}
                  className={[
                    "group/thumb snap-start shrink-0 w-[42%] min-w-[140px] max-w-[200px]",
                    "sm:w-[30%] sm:max-w-none",
                    "md:w-auto md:min-w-0 md:max-w-none",
                    "text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  ].join(" ")}
                  style={{
                    outlineColor: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)",
                  }}
                >
                  <div
                    className={[
                      "relative aspect-[16/10] overflow-hidden rounded-xl bg-[#111] transition",
                      selected
                        ? isDark
                          ? "ring-2 ring-white/85 ring-offset-2 ring-offset-black"
                          : "ring-2 ring-neutral-900/80 ring-offset-2 ring-offset-[#f2f2f0]"
                        : isDark
                          ? "ring-1 ring-white/10 group-hover/thumb:ring-white/35"
                          : "ring-1 ring-black/10 group-hover/thumb:ring-black/25",
                    ].join(" ")}
                  >
                    {thumbSrc ? (
                      still ? (
                        <img
                          src={still}
                          alt=""
                          loading="lazy"
                          className={[
                            "h-full w-full object-cover transition duration-500 group-hover/thumb:scale-[1.03]",
                            project.blur ? "blur-md scale-110" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        />
                      ) : (
                        <CoverMedia src={thumbSrc} blur={project.blur} mode="thumb" />
                      )
                    ) : (
                      <ImageFallback />
                    )}
                    <span className="absolute inset-0 bg-black/25 transition group-hover/thumb:bg-black/15" />
                    {src && isVideoUrl(src) && (
                      <span className="absolute inset-0 flex items-center justify-center opacity-90 transition group-hover/thumb:opacity-100 group-hover/thumb:scale-105">
                        <PlayButton size="sm" />
                      </span>
                    )}
                  </div>
                  <div className="mt-2 space-y-0.5 px-0.5">
                    <p
                      className="truncate text-[11px] font-semibold uppercase tracking-wide sm:text-[12px]"
                      style={{
                        color: selected ? ink : isDark ? "rgba(245,245,245,0.85)" : muted,
                        opacity: selected ? 1 : isDark ? 1 : 0.9,
                      }}
                    >
                      {project.title}
                    </p>
                    <p
                      className="truncate text-[10px] sm:text-[11px]"
                      style={{ color: muted, opacity: isDark ? 0.55 : 0.75 }}
                    >
                      {genreLine(project.tags)}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
